import type { ExternalPlacement, LinkEdge, ScopeUndo } from '../../types';

/**
 * Undo and redo for one link operation at a time.
 *
 * INVERSES, NOT SNAPSHOTS. There is already a snapshot undo, and it is the
 * right tool for the verbs it covers: Relink and Clear links change every edge
 * at once, so the only sane description of "before" is the whole graph. It is
 * the wrong tool here. A snapshot stores every edge in the map and only ten are
 * kept, so recording one per drawn link would cost a full copy of the graph per
 * click and still give ten steps of history. Worse, a snapshot restore is not
 * invertible, so there could be no redo at all.
 *
 * One drawn link inverts to one deletion. That is cheap, exact, and reversible
 * in both directions, which is what makes redo possible.
 *
 * IDS ARE NOT STABLE ACROSS AN UNDO. Undoing a link deletes a row; redoing it
 * INSERTS A NEW ONE with a new id, so an op held in the stack goes stale the
 * moment it is applied. Every commit therefore takes the op back and stores the
 * updated one — see `commitUndo`.
 *
 * ONE STACK, NOT TWO. There used to be a second undo — a snapshot restore
 * offered in the notice bar after a bulk verb — and it produced the worst
 * possible outcome: the user removed a source, reached for the Undo button they
 * could see, and found it disabled, because the action they had just taken had
 * been recorded somewhere else. Every verb now lands here.
 *
 * BULK VERBS STILL TRUNCATE IT. After a relink, every id below may name a row
 * that no longer exists, and an undo built on a stale id does not fail — it
 * deletes whatever holds that id now. So those verbs clear what came before and
 * record themselves as the only entry: the last thing you did stays undoable,
 * and nothing older pretends to be.
 */

export type LinkOp =
  | {
      kind: 'link';
      ownerId: number;
      targetId: number;
      bidirectional: boolean;
      /** Filled in once the server has created them, so an undo knows what to
       *  delete. Empty until the first apply completes. */
      edgeIds: number[];
    }
  | { kind: 'unlink'; edge: LinkEdge }
  | { kind: 'lock'; edgeId: number; locked: boolean }
  | { kind: 'place'; externalTargetId: number; pageId: number }
  | { kind: 'unplace'; placement: ExternalPlacement }
  /** Removing a source. Its own endpoint puts the scope AND its links back, so
   *  unlike the bulk verbs this one is genuinely reversible both ways. */
  | { kind: 'scope-removal'; scopeId: number; label: string; undo: ScopeUndo }
  /** A bulk verb — relink, clear, lock all — undone by restoring the snapshot
   *  it took first. Undo-only: re-running a relink would not reproduce what it
   *  did, so offering redo would be a lie. */
  | { kind: 'snapshot'; snapshotId: number; label: string };

export interface History {
  past: LinkOp[];
  future: LinkOp[];
}

export const EMPTY_HISTORY: History = { past: [], future: [] };

/**
 * How far back you can go.
 *
 * Deep enough that an afternoon's linking is recoverable, bounded because these
 * hold whole `LinkEdge` rows and a session that never reloads would otherwise
 * grow one forever.
 */
export const HISTORY_LIMIT = 50;

/** Record something that just happened. */
export function record(history: History, op: LinkOp): History {
  return {
    past: [...history.past, op].slice(-HISTORY_LIMIT),
    // A new action after an undo abandons the branch you had undone. Keeping it
    // would offer a redo that re-applies an operation to a graph that has since
    // moved on underneath it.
    future: [],
  };
}

export function peekUndo(history: History): LinkOp | null {
  return history.past[history.past.length - 1] ?? null;
}

export function peekRedo(history: History): LinkOp | null {
  return history.future[0] ?? null;
}

/**
 * Can this operation be done AGAIN after being undone?
 *
 * Only the snapshot ones cannot. Re-running a relink would produce a different
 * graph — the model is not deterministic — and re-clearing after an undo is
 * just Clear links, which is one click away. A redo button that silently did
 * something else is worse than one that stays disabled.
 */
export function isRedoable(op: LinkOp): boolean {
  return op.kind !== 'snapshot';
}

/** Wipe what came before and record this as the only entry. For verbs that
 *  invalidate every id below them. */
export function replaceHistory(op: LinkOp): History {
  return { past: [op], future: [] };
}

/**
 * Move the last operation onto the redo stack.
 *
 * `replacement` is the op as it stands AFTER being undone — the caller hands it
 * back because undoing can change the ids it will need next time. Redoing a
 * link creates rows with new ids; undoing an unlink recreates the edge with a
 * new id, so the `unlink` waiting in the redo stack must name the new one.
 */
export function commitUndo(history: History, replacement: LinkOp): History {
  if (!history.past.length) return history;
  return {
    past: history.past.slice(0, -1),
    future: [replacement, ...history.future].slice(0, HISTORY_LIMIT),
  };
}

export function commitRedo(history: History, replacement: LinkOp): History {
  if (!history.future.length) return history;
  return {
    past: [...history.past, replacement].slice(-HISTORY_LIMIT),
    future: history.future.slice(1),
  };
}

/** Everything in the stack is now suspect. See the note at the top. */
export function clearHistory(): History {
  return EMPTY_HISTORY;
}

/**
 * What the button will do, in words.
 *
 * Undo is a button whose effect is invisible until it fires, and these
 * operations are not interchangeable — putting a deleted link back and removing
 * one you just drew are opposite acts. The tooltip says which.
 */
export function describeOp(op: LinkOp, direction: 'undo' | 'redo'): string {
  const undoing = direction === 'undo';
  switch (op.kind) {
    case 'link':
      return undoing
        ? `Undo: remove the link you just drew${op.bidirectional ? ' (both ways)' : ''}`
        : `Redo: draw that link again${op.bidirectional ? ' (both ways)' : ''}`;
    case 'unlink':
      return undoing ? 'Undo: put that link back' : 'Redo: remove that link again';
    case 'lock':
      // `locked` is the state the operation PRODUCED, so undoing goes the other
      // way — naming it the wrong way round would be worse than saying nothing.
      return undoing
        ? `Undo: ${op.locked ? 'unlock' : 'lock'} that link again`
        : `Redo: ${op.locked ? 'lock' : 'unlock'} that link`;
    case 'place':
      return undoing
        ? 'Undo: take that external link off the page'
        : 'Redo: put that external link back on the page';
    case 'unplace':
      return undoing
        ? 'Undo: put that external link back on the page'
        : 'Redo: take that external link off the page again';
    case 'scope-removal':
      return undoing
        ? `Undo: put “${op.label}” back, with its links`
        : `Redo: remove “${op.label}” again`;
    case 'snapshot':
      // No redo branch that means anything — see `isRedoable`.
      return `Undo: ${op.label}`;
  }
}
